import React, { useMemo, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  InputAdornment,
  IconButton,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { useForm, Controller } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useResetPasswordMutation } from "../redux/api/authApi";
import ThemeProviderWrapper from "../theme/ThemeProviderWrapper";
import AuthHeader from "../components/layout/AuthHeader";
// import AuthFooter from "../components/layout/AuthFooter";
import AuthCard from "../components/layout/AuthCard";
import { AuthRoleBadge, parseRoleFromSearch } from "../components/auth/AuthRoleBadge";

// Define Yup validation schema
const schema = yup
  .object({
    password: yup
      .string()
      .min(8, "Password must be at least 8 characters")
      .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
      .matches(/[a-z]/, "Password must contain at least one lowercase letter")
      .matches(/[0-9]/, "Password must contain at least one number")
      .matches(
        /[!@#$%^&*(),.?":{}|<>]/,
        "Password must contain at least one special character"
      )
      .required("Password is required"),
  })
  .required();

export default function ForgotPassword() {
  const { token } = useParams(); // Get token from URL
  const [searchParams] = useSearchParams();
  const roleKey = useMemo(
    () => parseRoleFromSearch(searchParams.get("role")),
    [searchParams]
  );
  const [resetPassword, { isLoading, error }] = useResetPasswordMutation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data) => {
    const { password } = data;

    try {
      // Call the mutation function with password and token
      const response = await resetPassword({ token, password }).unwrap();
      // Redirect or show success based on response
      if (response.success) {
        navigate("/"); // Navigate to login after success
      } else {
        alert("Password reset failed");
      }
    } catch (err) {
      console.error("Error resetting password:", err);
      alert("Something went wrong");
    }
  };

  return (
    <ThemeProviderWrapper>
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          position: "relative",
          "&:before": {
            content: '""',
            position: "absolute",
            inset: 0,
            background:
              "radial-gradient(circle at 50% 20%, rgba(56,189,248,0.10), transparent 60%)," +
              "radial-gradient(circle at 80% 80%, rgba(168,85,247,0.10), transparent 70%)",
            zIndex: -2,
          },
          "&:after": {
            content: '""',
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(0deg, transparent 0, rgba(255,255,255,0.03) 1px)," +
              "linear-gradient(90deg, transparent 0, rgba(255,255,255,0.03) 1px)",
            backgroundSize: "80px 80px",
            opacity: 0.15,
            zIndex: -1,
          },
        }}
      >
        <AuthHeader />
        <AuthCard>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6" fontWeight={700}>
              New Password
            </Typography>
            <AuthRoleBadge roleKey={roleKey} />
          </Stack>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Enter your new Password
          </Typography>
          <Box>
            <Typography
              variant="body2"
              sx={{ mb: 0.6, fontWeight: 700, color: "#cbd5e1" }}
            >
              Password
            </Typography>
            <Controller
              name="password"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type={showPassword ? "text" : "password"}
                  placeholder="********"
                  fullWidth
                  error={!!errors.password}
                  helperText={errors.password?.message}
                  InputProps={{
                    sx: {
                      background: "rgba(255,255,255,0.05)",
                      borderRadius: "10px",
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                          onClick={() => setShowPassword((p) => !p)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? (
                            <VisibilityOff sx={{ color: "#94a3b8" }} />
                          ) : (
                            <Visibility sx={{ color: "#94a3b8" }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
          </Box>
          <Stack direction="row" spacing={2} sx={{ mt: 4 }}>
            <Button
              fullWidth
              variant="contained"
              sx={{
                background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
                borderRadius: "12px",
                fontWeight: 600,
                textTransform: "none",
              }}
              onClick={() => navigate("/")}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              sx={{
                background: "linear-gradient(90deg,#0284c7,#0ea5e9)",
                borderRadius: "12px",
                fontWeight: 600,
                textTransform: "none",
              }}
              onClick={handleSubmit(onSubmit)}
              disabled={isLoading}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </Button>
          </Stack>
          {error && <Typography color="error">{error.message}</Typography>}
        </AuthCard>
        {/* <AuthFooter /> */}
      </Box>
    </ThemeProviderWrapper>
  );
}
